import type { Client, PickerFileMetadata, PickerResponse } from 'filestack-js';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { FacebookCampaign, FacebookDisplayCreative, IFacebook } from '@feathr/blackbox';
import { CreativeClass, FacebookVideoCreative } from '@feathr/blackbox';
import { Button, MenuItem, toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

interface IProps {
  creatives: IObservableArray<FacebookDisplayCreative>;
  campaign: FacebookCampaign;
  isMenuItem?: boolean;
}

interface IVideoDimensionDefintion {
  width: number;
  height: number;
  duration: number;
}

function getVideoDimensions(url: string): Promise<IVideoDimensionDefintion> {
  return new Promise<IVideoDimensionDefintion>((resolve) => {
    const video = document.createElement('video');
    video.addEventListener('loadedmetadata', () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    });
    video.src = url;
  });
}

async function uploadToFacebook(
  creative: FacebookVideoCreative,
  fbAccountId?: string,
  accessToken?: string,
): Promise<void> {
  if (!fbAccountId || !accessToken) {
    toast('Failed to upload video to Meta.', {
      type: ToastType.ERROR,
    });
    return;
  }
  const body = new FormData();
  body.append('access_token', accessToken);
  body.append('file_url', creative.get('url'));
  const response = await fetch(
    `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${fbAccountId}/advideos`,
    {
      body,
      method: 'POST',
    },
  );
  const json = await response.json();
  const videoId = json.id;
  if (videoId) {
    creative.set({ ad_video_id: videoId });
    toast('Video uploaded to Meta', { type: ToastType.SUCCESS });
  }
}

/** 1GB for videos */
const VIDEO_SIZE_LIMIT = 1024 * 1024 * 1024;
const VIDEO_CONTENT_TYPES = ['video', 'gif'];

/** 20MB for images */
const IMAGE_SIZE_LIMIT = 20 * 1024 * 1024;

function AddCreativeButton({ creatives, campaign, isMenuItem }: IProps): JSX.Element {
  const { Creatives } = useStore();
  const account = useAccount();
  const integration = account.get('facebook', {} as IFacebook);
  const { t } = useTranslation();

  function setCreatives(client: Client) {
    return (files: PickerResponse) => {
      const { filesUploaded } = files;
      filesUploaded.forEach(async (file: PickerFileMetadata): Promise<void> => {
        const { key, container, handle } = file;
        const url = `https://s3.amazonaws.com/${container}/${encodeURIComponent(key!)}`;
        const metadata = await client.metadata(handle, {
          width: true,
          height: true,
          mimetype: true,
          size: true,
          filename: true,
        });
        let cls = CreativeClass.FacebookImage;
        let { width, height } = metadata;
        let duration = 0;
        const { mimetype, filename } = metadata;
        const isVideo = mimetype.includes('video');
        const isGif = mimetype.includes('gif');
        if (isVideo) {
          const videoDimensions = await getVideoDimensions(url);
          width = videoDimensions.width;
          height = videoDimensions.height;
          duration = videoDimensions.duration;
          cls = CreativeClass.FacebookVideo;
        }
        if (isGif) {
          cls = CreativeClass.FacebookVideo;
        }
        const creative = Creatives.create({
          width,
          height,
          duration,
          mimetype,
          url,
          _cls: cls,
          parent: campaign.id,
          name: `${filename} (${width}x${height})`,
          is_enabled: true,
          account: account.id,
        }) as FacebookDisplayCreative;
        const validDimensions = creative.getValidDimensions(campaign);

        // Check if aspect ratio is valid instead of exact dimensions
        const creativeAspectRatio = creative.aspectRatioDecimal;

        // Calculate actual aspect ratios from dimensions
        const validAspectRatioValues = Object.values(validDimensions).map(
          (dim) => dim.width / dim.height,
        );

        // Perform exact aspect ratio matching with no tolerance
        const isAspectRatioValid = validAspectRatioValues.includes(creativeAspectRatio);

        // Get minimum dimensions for the matching aspect ratio and select appropriate spec
        let minWidth = 0;
        let minHeight = 0;
        let matchingSpec = '';

        if (isAspectRatioValid) {
          // Find all dimensions with the matching aspect ratio
          const matchingDimensions = Object.values(validDimensions).filter(
            (dim) => dim.width / dim.height === creativeAspectRatio,
          );

          // Get the minimum width and height
          minWidth = Math.min(...matchingDimensions.map((dim) => dim.width));
          minHeight = Math.min(...matchingDimensions.map((dim) => dim.height));

          // Find the best matching dimension spec (prefer exact match, then closest larger dimension)
          const exactMatch = matchingDimensions.find(
            (dim) => dim.width === width && dim.height === height,
          );

          if (exactMatch) {
            // Use exact match if available
            matchingSpec = exactMatch.spec;
          } else {
            // Sort by dimension size (smallest valid dimensions first)
            const sortedBySize = [...matchingDimensions].sort((a, b) => {
              // Sort by area to get overall smallest dimensions
              return a.width * a.height - b.width * b.height;
            });

            // Find the smallest dimension that's still large enough for our image
            const bestMatch = sortedBySize.find(
              (dim) => dim.width >= width && dim.height >= height,
            );
            // Fallback to the smallest if none are big enough
            matchingSpec = (bestMatch || sortedBySize[0]).spec;
          }
        }

        // Check if the creative dimensions meet the minimum requirements
        const meetsMinDimensions = width >= minWidth && height >= minHeight;
        const isValid = isAspectRatioValid && meetsMinDimensions;

        if (!isValid) {
          const closestAspectRatio = creative.getClosestValidAspectRatio(validDimensions);

          // Format the aspect ratio for display
          const formattedCreativeRatio = creative.aspectRatio;

          let errorMessage = `"${filename}" has an unsupported aspect ratio (${formattedCreativeRatio}).`;

          if (isAspectRatioValid && !meetsMinDimensions) {
            errorMessage = `"${filename}" is too small. For ${formattedCreativeRatio} aspect ratio, minimum dimensions are ${minWidth}x${minHeight}.`;
          } else {
            errorMessage += ` The closest valid aspect ratio would be ${closestAspectRatio}`;
          }

          toast(errorMessage, { type: 'error' });
        } else {
          // Set the spec to match the appropriate spec with the same aspect ratio
          if (matchingSpec) {
            creative.set({ spec: matchingSpec });
          }

          if (creative instanceof FacebookVideoCreative) {
            await uploadToFacebook(creative, integration.id, account?.getFacebookAccessToken);
          }
          creatives.push(creative);
        }
      });
    };
  }

  async function handleClick(): Promise<void> {
    const filestackJs = await import(/* webpackChunkName: "filestack-js" */ 'filestack-js');
    const client = filestackJs.init(FILESTACK_API_KEY);

    const handleFileNameChange = async (file: PickerFileMetadata): Promise<PickerFileMetadata> => {
      const sanitizeFilename = (filename: string): string =>
        filename
          // Match any character that is NOT alphanumeric, hyphen, underscore, space, or dot.
          .replaceAll(/[^a-zA-Z0-9_\-\s.]/g, '')
          // Then replace any whitespace.
          .replaceAll(/\s+/g, '_');

      // Uploads from URLs and 3rd-party apps do not contain a filename.
      if (!file.filename && file.originalPath) {
        const filename = file.originalPath.split('/').pop() ?? '';

        return { ...file, filename: sanitizeFilename(filename) };
      }

      // Check file size based on mimetype
      const isVideo = VIDEO_CONTENT_TYPES.some((type) => file.mimetype?.includes(type));

      if (isVideo && file.size > VIDEO_SIZE_LIMIT) {
        throw new Error('File too large. Video size limit is 1GB.');
      } else if (!isVideo && file.size > IMAGE_SIZE_LIMIT) {
        throw new Error('File too large. Image size limit is 20MB.');
      }

      return {
        ...file,
        filename: sanitizeFilename(file.filename),
      };
    };
    client
      .picker({
        onUploadDone: setCreatives(client),
        // Renaming currently only works for local uploads
        onFileSelected: handleFileNameChange,
        cleanupImageExif: true,
        maxFiles: 10,
        // Set overall max size to 1GB
        maxSize: VIDEO_SIZE_LIMIT,
        // TODO: add `video/quicktime` back when we implement proper mov support
        accept: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
        transformations: {
          crop: false,
          circle: false,
          rotate: false,
        },
        dropPane: {},
      })
      .open();
  }

  return isMenuItem ? (
    <MenuItem id={'addMedia'} onClick={handleClick}>
      {t('Add image/video creative')}
    </MenuItem>
  ) : (
    <Button
      disabled={!campaign.get('facebook_page_id')}
      id={'addMedia'}
      key={'add'}
      name={'add_creative_button'}
      onClick={handleClick}
      prefix={getIconForAction('add')}
    >
      {t('Add creative')}
    </Button>
  );
}

export default observer(AddCreativeButton);
